import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ban, CheckCircle2, ChevronRight, Eye, FileLock, FileText, Heart, Images, KeyRound, Lock, LogOut, Mail, MessageCircle,
  Phone, Bell, ShieldCheck, Shield, SlidersHorizontal, User, Users, UsersRound,
} from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MISSION_INFO } from "@/lib/constants";
import { SUPPORT_EMAIL, supportMailto } from "@/lib/checkout";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { DashboardSummary } from "@/components/dashboard";
import { Button, Loading, PageHeader, Pill, Toggle } from "@/components/ui";

function Row({ icon: Icon, label, value, onClick, testId, right }) {
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap data-testid={testId} onClick={onClick} className={`flex w-full items-center gap-3 border-b border-border px-4 py-3.5 text-left last:border-b-0 ${onClick ? "transition-colors hover:bg-[#FDFBF7]" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F6E3E4]"><Icon className="h-5 w-5 text-primary" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium">{label}</span>
        {value ? <span className="block truncate text-xs text-muted-foreground">{value}</span> : null}
      </span>
      {right ?? (onClick ? <ChevronRight className="h-[18px] w-[18px] text-muted-foreground" /> : null)}
    </Wrap>
  );
}

const Group = ({ title, children }) => (
  <section className="mb-6">
    <h2 className="mb-2 px-1 text-[22px]">{title}</h2>
    <div className="overflow-hidden rounded-[20px] border border-border bg-white">{children}</div>
  </section>
);

export default function Profile() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, setUser, signOut } = useAuth();
  const [confirmOut, setConfirmOut] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!user) return <Loading />;
  const mission = user.mission ? MISSION_INFO[user.mission] : null;
  const edit = (step) => navigate(`/onboarding?mode=edit&step=${step}`);
  const toggleConsent = async (v) => {
    setSaving(true);
    try {
      setUser(await api("/profile/whatsapp-consent", { method: "PUT", body: { whatsapp_sharing_consent: v } }));
      toast(v ? "WhatsApp sharing enabled for connections" : "WhatsApp sharing turned off", "success");
    } catch (e) { toast(e.message, "error"); } finally { setSaving(false); }
  };
  const logout = async () => { setConfirmOut(false); await signOut(); navigate("/landing", { replace: true }); };

  return (
    <div data-testid="profile-screen">
      <PageHeader title="Profile" />
      <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <div className="mb-4 flex items-center gap-4 rounded-[20px] border border-border bg-white p-5">
            <img src={photoUri(user.main_photo_url)} alt="" className="h-24 w-24 shrink-0 rounded-full border-2 border-[#D48D93] bg-[#F4EBE6] object-cover" />
            <div className="min-w-0 flex-1">
              <p className="font-display text-[30px] leading-tight" data-testid="profile-name">{user.display_name}, {user.age}</p>
              <p className="text-sm text-muted-foreground">{user.city}, {user.country}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {mission ? <Pill label={mission.label} tone="rose" icon={mission.Icon} testId="profile-mission-pill" /> : null}
                <button data-testid="profile-badge-get-verified" onClick={() => navigate("/verification")}>
                  {user.verification_status === "verified"
                    ? <Pill label="Verified Member" tone="gold" icon={CheckCircle2} testId="profile-verification-pill" />
                    : <Pill label="Unverified · Get Verified" icon={Shield} testId="profile-verification-pill" />}
                </button>
              </div>
            </div>
          </div>
          {mission ? <p className="mb-4 text-sm" data-testid="profile-request-label">Your request button: <b>{user.request_label}</b></p> : null}
          <DashboardSummary />
        </div>
        <div>
          <Group title="Your profile">
            <Row testId="profile-my-profile" icon={Eye} label="My Profile" value="See how members view you" onClick={() => navigate(`/member/${user.id}`)} />
            <Row testId="profile-edit-personal" icon={User} label="Edit Profile" value={user.profile?.occupation} onClick={() => edit(0)} />
            <Row testId="profile-edit-mission" icon={mission?.Icon ?? Heart} label="Mission" value={mission?.label} onClick={() => edit(1)} />
            <Row testId="profile-edit-preferences" icon={SlidersHorizontal} label="Preferences" value={`Ages ${user.preferences?.min_age ?? "-"}–${user.preferences?.max_age ?? "-"}`} onClick={() => edit(2)} />
            <Row testId="profile-edit-photos" icon={Images} label="Photos" value={`${user.photos.length} photo${user.photos.length === 1 ? "" : "s"}`} onClick={() => edit(3)} />
          </Group>
          <Group title="Activity">
            <Row testId="profile-saved-profiles" icon={Heart} label="Saved Profiles" onClick={() => navigate("/saved")} />
            <Row testId="profile-requests" icon={Mail} label="Requests" onClick={() => navigate("/requests")} />
            <Row testId="profile-connections" icon={Users} label="Connections" onClick={() => navigate("/connections")} />
            <Row testId="profile-notifications" icon={Bell} label="Notifications" onClick={() => navigate("/notifications")} />
          </Group>
          <Group title="Privacy & safety">
            <Row testId="profile-privacy-settings" icon={Lock} label="Privacy Settings" value="Online status, discovery, emails" onClick={() => navigate("/settings/privacy")} />
            <Row testId="profile-blocked-members" icon={Ban} label="Blocked Members" onClick={() => navigate("/settings/blocked")} />
            <Row testId="profile-account-security" icon={KeyRound} label="Account Security" value="Password, data export, delete account" onClick={() => navigate("/settings/security")} />
            <Row testId="profile-safety-centre" icon={ShieldCheck} label="Safety Centre" onClick={() => navigate("/safety")} />
            <Row testId="profile-whatsapp-consent-row" icon={MessageCircle} label="Share WhatsApp with connections" value="Only after mutual acceptance"
              right={<Toggle testId="profile-whatsapp-consent-switch" label="Share WhatsApp with connections" disabled={saving} checked={user.whatsapp_sharing_consent} onChange={toggleConsent} />} />
            <Row testId="profile-account-email" icon={Mail} label="Email" value={user.email} />
            <Row testId="profile-account-phone" icon={Phone} label="Phone" value={user.phone_masked ?? "-"} />
          </Group>
          <Group title="Legal">
            <Row testId="profile-terms" icon={FileText} label="Terms and Conditions" onClick={() => navigate("/terms")} />
            <Row testId="profile-privacy-policy" icon={FileLock} label="Privacy Policy" onClick={() => navigate("/privacy")} />
            <Row testId="profile-guidelines" icon={UsersRound} label="Community Guidelines" onClick={() => navigate("/guidelines")} />
            <Row testId="profile-child-safety" icon={Shield} label="Child Safety Standards" onClick={() => navigate("/child-safety")} />
            <Row testId="profile-contact-support" icon={Mail} label="Contact Support" value={SUPPORT_EMAIL} onClick={() => { window.location.href = supportMailto(); }} />
          </Group>
          <Button testId="profile-logout-button" title="Log out" variant="outline" icon={LogOut} className="w-full" onClick={() => setConfirmOut(true)} />
        </div>
      </div>
      <Modal open={confirmOut} onClose={() => setConfirmOut(false)} title="Log out?" testId="logout-sheet">
        <p className="mb-6 text-sm text-muted-foreground">Your profile, requests and connections are saved. Log back in anytime.</p>
        <div className="flex gap-3">
          <Button testId="logout-cancel-button" title="Cancel" variant="outline" className="flex-1" onClick={() => setConfirmOut(false)} />
          <Button testId="logout-confirm-button" title="Log out" className="flex-1" onClick={logout} />
        </div>
      </Modal>
    </div>
  );
}
